<template>
    <div>
        <div class="frame">
            <p>{{ $t("credits.intro") }}</p>
            <h1>{{ $t("credits.translationHeader") }}</h1>
            <table>
                <thead>
                    <tr>
                        <th>{{ $t("credits.authorHeader") }}</th>
                        <th>{{ $t("credits.languageHeader") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(t, idx) in translators" :key="idx">
                        <th>{{ t.name }}</th>
                        <td>{{ $t("credits.lang."+t.lang) }}</td>
                        <td>
                            <email-icon
                                v-if="t.addr.length > 0"
                                class="x-small-icon theme-fio-button"
                                role="button"
                                tabindex="0"
                                @click="onShowEmailInfo(t.addr)"
                                @keypress.enter="onShowEmailInfo(t.addr)"
                                @keypress.space="onShowEmailInfo(t.addr)">
                            </email-icon>
                            <a
                                v-if="t.linkedIn.length > 0"
                                :href="'https://www.linkedin.com/in/'+t.linkedIn"
                                target="_blank">
                                <linked-in-icon class="x-small-icon theme-fio-button">
                                </linked-in-icon>
                                <span class="registered-trademark">&#174;</span>
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>
            <h1>{{ $t("credits.artworkHeader") }}</h1>
            <table>
                <thead>
                    <tr>
                        <th>{{ $t("credits.authorHeader") }}</th>
                        <th>{{ $t("credits.contribHeader") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(a, idx) in artists" :key="idx">
                        <th>{{ a.name }}</th>
                        <td>
                            <div v-for="(c, idx2) in a.contrib" :key="idx2">{{ $t("credits.art."+c) }}</div>
                        </td>
                        <td>
                            <email-icon
                                v-if="a.addr.length > 0"
                                class="x-small-icon theme-fio-button"
                                role="button"
                                tabindex="0"
                                @click="onShowEmailInfo(a.addr)"
                                @keypress.enter="onShowEmailInfo(a.addr)"
                                @keypress.space="onShowEmailInfo(a.addr)">
                            </email-icon>
                            <a
                                v-if="a.linkedIn.length > 0"
                                :href="'https://www.linkedin.com/in/'+a.linkedIn"
                                target="_blank">
                                <linked-in-icon class="x-small-icon theme-fio-button">
                                </linked-in-icon>
                                <span class="registered-trademark">&#174;</span>
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <contact-email-popin
            v-if="emailUser.length > 0 && emailDomain.length > 0"
            :title="$t('credits.emailLabel') + $t('colonSuffix')"
            :user="emailUser"
            :domain="emailDomain"
            @close="onHideEmailInfo"></contact-email-popin>
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import ContactEmailPopin from "@/components/ContactEmailPopin.vue";

import EmailIcon from "@/scalar/social-email.svg";
import LinkedInIcon from "@/scalar/social-linkedin.svg";

interface IContactable {
    addr: string;
    linkedIn: string;
}

interface ITranslator extends IContactable {
    name: string;
    lang: string;
}

interface IArtist extends IContactable {
    name: string;
    contrib: string[];
}

const emailUser = ref("");
const emailDomain = ref("");

const translators: ITranslator[] = [
    { name: "Christian Sester", lang: "de", addr: "", linkedIn: "" },
    { name: "Eric Chane-Thien", lang: "fr", addr: "", linkedIn: "" },
    { name: "Emanuele Arioli", lang: "it", addr: "", linkedIn: "" },
    { name: "Sofia El Bamrani", lang: "ja", addr: "gmail.com|sofia7sawako", linkedIn: "" },
    { name: "Misael Dos Santos Matos", lang: "ptBr", addr: "", linkedIn: "" },
    {
        name: "Helena Aleman Heiskanen",
        lang: "es",
        addr: "hotmail.com|helheis",
        linkedIn: "helena-aleman-heiskanen-70956712a"
    },
    { name: "Do Khoa Nam", lang: "vi", addr: "", linkedIn: "" },
    { name: "Chris Poon", lang: "zh", addr: "", linkedIn: "" }
];

const artists: IArtist[] = [{
    name: "Nhat-Tam Huynh",
    contrib: ["corpLogo", "appLogo"],
    addr: "yahoo.com|Leonnhattam",
    linkedIn: "hntam"
}];

function onShowEmailInfo(addr: string): void {
    const parts = addr.split("|");
    if (parts.length < 2) { return; }

    emailUser.value = parts[1];
    emailDomain.value = parts[0];
}

function onHideEmailInfo(): void {
    emailUser.value = "";
    emailDomain.value = "";
}
</script>

<style scoped>
.x-small-icon {
    margin: 0 .2em;
    vertical-align: middle;
}

.frame {
    display: block;
    padding-left: max(calc(50vw - 400px), 1em);
    padding-right: max(calc(50vw - 400px), 1em);
    height: calc(var(--body-size) - .5em);
    overflow-y: auto;
}

thead > tr > th {
    text-align: left;
}

tbody > tr:nth-child(odd) {
    background-color: var(--selected-colour);
}

tbody > tr {
    line-height: 2em;
}

tbody > tr > th {
    text-align: left;
    font-weight: normal;
    padding: .25em .5em .25em 0em;
}

tbody > tr > td {
    text-align: left;
    padding: .25em .5em .25em 0em;
}

tbody > tr > td:last-child {
    min-width: 7.5em;
}

.registered-trademark {
    width: 1em;
    height: 1em;
    display: inline-block;
    font-size: 5pt;
    color: var(--ink-colour);
    left: -.4em;
    bottom: -.9em;
    position: relative;
}
</style>
